
import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/hooks/useAuth";

interface GrowthPillar {
  id: string;
  name: string;
  description: string;
  sales_channel_id: string;
  created_at: string;
  updated_at: string;
}

interface CreateGrowthPillarData {
  name: string;
  description: string;
  sales_channel_id: string;
}

interface UpdateGrowthPillarData {
  name: string;
  description: string;
}

export const useGrowthPillars = (salesChannelId?: string) => {
  const [growthPillars, setGrowthPillars] = useState<GrowthPillar[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const { toast } = useToast();
  const { user } = useAuth();

  const fetchGrowthPillars = async () => {
    if (!user || !salesChannelId) {
      setGrowthPillars([]);
      return;
    }

    try {
      setIsLoading(true);
      const { data, error } = await supabase
        .from('growth_pillars')
        .select('*')
        .eq('sales_channel_id', salesChannelId)
        .order('created_at', { ascending: false });

      if (error) throw error;
      setGrowthPillars(data || []);
    } catch (error: any) {
      console.error('Error fetching growth pillars:', error);
      toast({
        title: "Error",
        description: "Failed to fetch growth pillars",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const createGrowthPillar = async (data: CreateGrowthPillarData): Promise<boolean> => {
    if (!user) {
      toast({
        title: "Error",
        description: "Please log in first",
        variant: "destructive",
      });
      return false;
    }

    try {
      setIsLoading(true);
      const { error } = await supabase
        .from('growth_pillars')
        .insert([data]);

      if (error) throw error;

      toast({
        title: "Success",
        description: "Growth pillar created successfully",
      });

      await fetchGrowthPillars();
      return true;
    } catch (error: any) {
      console.error('Error creating growth pillar:', error);
      toast({
        title: "Error",
        description: "Failed to create growth pillar",
        variant: "destructive",
      });
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  const updateGrowthPillar = async (id: string, data: UpdateGrowthPillarData): Promise<boolean> => {
    if (!user) return false;

    try {
      setIsLoading(true);
      const { error } = await supabase
        .from('growth_pillars')
        .update(data)
        .eq('id', id);

      if (error) throw error;

      toast({
        title: "Success",
        description: "Growth pillar updated successfully",
      });

      await fetchGrowthPillars();
      return true;
    } catch (error: any) {
      console.error('Error updating growth pillar:', error);
      toast({
        title: "Error",
        description: "Failed to update growth pillar",
        variant: "destructive",
      });
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  const deleteGrowthPillar = async (id: string): Promise<boolean> => {
    if (!user) return false;

    try {
      setIsLoading(true);
      const { error } = await supabase
        .from('growth_pillars')
        .delete()
        .eq('id', id);

      if (error) throw error;

      toast({
        title: "Success",
        description: "Growth pillar deleted successfully",
      });

      await fetchGrowthPillars();
      return true;
    } catch (error: any) {
      console.error('Error deleting growth pillar:', error);
      toast({
        title: "Error",
        description: "Failed to delete growth pillar",
        variant: "destructive",
      });
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchGrowthPillars();
  }, [user, salesChannelId]);

  return {
    growthPillars,
    isLoading,
    createGrowthPillar,
    updateGrowthPillar,
    deleteGrowthPillar,
    refetch: fetchGrowthPillars,
  };
};
